import React, { Component } from 'react';
import { logRenderError, downloadLogs } from '../../utils/logger';

/**
 * Error Boundary Component
 *
 * Catches JavaScript errors anywhere in the child component tree,
 * logs them, and displays a fallback UI instead of crashing the whole app.
 */
class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        // Log the error to our logging system
        const componentName = this.props.componentName || 'Application';
        logRenderError(error, errorInfo, componentName);

        this.setState({
            error,
            errorInfo
        });
    }

    handleRetry = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null
        });
    };

    handleDownloadLogs = () => {
        downloadLogs();
    };

    handleGoHome = () => {
        const id = localStorage.getItem("encryptedData");
        if (id) {
            window.location.href = `/${id}`;
        } else {
            window.location.href = '/';
        }
    };

    render() {
        if (this.state.hasError) {
            // Custom fallback UI
            if (this.props.fallback) {
                return this.props.fallback;
            }

            // Default fallback UI
            return (
                <div style={styles.container}>
                    <div style={styles.content}>
                        <div style={styles.iconContainer}>
                            <svg
                                style={styles.icon}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                />
                            </svg>
                        </div>
                        <h1 style={styles.title}>Oops! Something went wrong</h1>
                        <p style={styles.message}>
                            We apologize for the inconvenience. An unexpected error has occurred.
                        </p>
                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <div style={styles.errorDetails}>
                                <p style={styles.errorMessage}>
                                    <strong>Error:</strong> {this.state.error.toString()}
                                </p>
                                {this.state.errorInfo && (
                                    <details style={styles.stackTrace}>
                                        <summary style={styles.summary}>View Stack Trace</summary>
                                        <pre style={styles.pre}>
                                            {this.state.errorInfo.componentStack}
                                        </pre>
                                    </details>
                                )}
                            </div>
                        )}
                        <div style={styles.buttonContainer}>
                            <button
                                onClick={this.handleRetry}
                                style={styles.primaryButton}
                            >
                                Try Again
                            </button>
                            <button
                                onClick={this.handleGoHome}
                                style={styles.secondaryButton}
                            >
                                Go to Home
                            </button>
                            <button
                                onClick={this.handleDownloadLogs}
                                style={styles.outlineButton}
                            >
                                Download Error Log
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

const styles = {
    container: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f8f9fa',
        padding: '20px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    },
    content: {
        textAlign: 'center',
        maxWidth: '500px',
        padding: '40px',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
    },
    iconContainer: {
        marginBottom: '20px'
    },
    icon: {
        width: '64px',
        height: '64px',
        color: '#dc3545',
        margin: '0 auto'
    },
    title: {
        fontSize: '24px',
        fontWeight: '600',
        color: '#212529',
        marginBottom: '12px'
    },
    message: {
        fontSize: '16px',
        color: '#6c757d',
        marginBottom: '24px',
        lineHeight: '1.5'
    },
    errorDetails: {
        textAlign: 'left',
        backgroundColor: '#fff3cd',
        border: '1px solid #ffc107',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '24px'
    },
    errorMessage: {
        fontSize: '14px',
        color: '#856404',
        margin: '0 0 12px 0',
        wordBreak: 'break-word'
    },
    stackTrace: {
        marginTop: '12px'
    },
    summary: {
        cursor: 'pointer',
        fontSize: '14px',
        color: '#856404',
        fontWeight: '500'
    },
    pre: {
        fontSize: '12px',
        color: '#856404',
        overflow: 'auto',
        maxHeight: '200px',
        marginTop: '8px',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word'
    },
    buttonContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
    },
    primaryButton: {
        padding: '12px 24px',
        fontSize: '16px',
        fontWeight: '500',
        color: '#ffffff',
        backgroundColor: '#007bff',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'background-color 0.2s'
    },
    secondaryButton: {
        padding: '12px 24px',
        fontSize: '16px',
        fontWeight: '500',
        color: '#ffffff',
        backgroundColor: '#6c757d',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'background-color 0.2s'
    },
    outlineButton: {
        padding: '12px 24px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#6c757d',
        backgroundColor: 'transparent',
        border: '1px solid #6c757d',
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'all 0.2s'
    }
};

export default ErrorBoundary;
